import type { LocalHorizontal } from "../coordinates.ts";
import {
  pointInRing,
  polygonAreaM2,
  ringCentroid,
  type LinearRing,
} from "../geometry.ts";
import type { BuildingFootprintData } from "../schema/tile.ts";

/**
 * Reviewed post-war residential style zones in world-local metres.
 *
 * Source: Vilnius Plan, "Vilniaus miesto seniūnijų ribos", feature layer 1
 * (EPSG:3346). The eight compact source rings were requested with a 20 m
 * cartographic tolerance and the much larger Antakalnis ring with 50 m, then
 * transformed relative to the checked-in wide-world origin E 582400 /
 * N 6062080. Administrative membership is only a first guardrail:
 * `isSovietPanelBackgroundCandidate` also requires source-footprint massing
 * consistent with the documented residential series, so mixed infill is not
 * relabelled as panel housing.
 */
export interface SovietPanelResidentialStyleZone {
  readonly id:
    | "zirmunai"
    | "fabijoniskes"
    | "seskine"
    | "pasilaiciai"
    | "justiniskes"
    | "virsuliskes"
    | "karoliniskes"
    | "lazdynai"
    | "antakalnis";
  readonly label: string;
  readonly sourceNumber: number;
  readonly outer: LinearRing;
  readonly supportedFloorCounts: readonly number[];
}

const STANDARD_PANEL_FLOOR_COUNTS = Object.freeze([5, 9, 12]);

export const SOVIET_PANEL_RESIDENTIAL_STYLE_ZONES:
  readonly SovietPanelResidentialStyleZone[] = Object.freeze([
    {
      id: "zirmunai",
      label: "Žirmūnai",
      sourceNumber: 2,
      supportedFloorCounts: STANDARD_PANEL_FLOOR_COUNTS,
      outer: points([
        [1427.5, -4707.1], [1117.2, -4639.9], [1008.2, -4651.8],
        [987.3, -4564.9], [907.2, -4548.3], [951.1, -4419],
        [907.2, -4247.4], [911.8, -4100.5], [939.5, -3968.4],
        [1003.3, -3868.2], [868.2, -3805.4], [624, -3805.8],
        [360.3, -3745], [448.3, -3640], [497.3, -3497],
        [522.3, -2944.1], [464.3, -2289.3], [386.3, -1999.3],
        [352.2, -1718.3], [617.5, -1689.1], [539.3, -1538.4],
        [509.3, -1387.4], [713.2, -431.6], [665.6, -69.8],
        [896, -97], [1006.4, -154], [1118.3, -307.6],
        [1519, -715.2], [1700.6, -1040.6], [1777.4, -1269.8],
        [1777.4, -1752.3], [1823.8, -1955.8], [2030.1, -2471.8],
        [2150.1, -3094], [2065.4, -3592.5], [2204.5, -3972.6],
        [2194.9, -4104.2], [2115.1, -4260.5], [1849.8, -4531.1],
        [1427.5, -4707.1],
      ]),
    },
    {
      id: "fabijoniskes",
      label: "Fabijoniškės",
      sourceNumber: 4,
      supportedFloorCounts: Object.freeze([5, 9, 12, 13, 16]),
      outer: points([
        [-2936.4, -7595.2], [-2388.7, -7509.2], [-2435.4, -6838.6],
        [-2583.6, -6817.5], [-2582.8, -6690], [-2774.7, -6696.8],
        [-2857.7, -6667.3], [-2900.4, -6617.2], [-2945.4, -6458.8],
        [-2908.9, -6443.7], [-2925.8, -6302.2], [-2993.8, -6305.3],
        [-3001.8, -6262.1], [-3064.7, -6272.3], [-3059.6, -6228.1],
        [-3136.6, -6144.9], [-3184, -6204], [-3282.2, -6129.5],
        [-3095.7, -5968.5], [-2776.2, -5930.8], [-2725.2, -5864.2],
        [-2583.5, -5980.7], [-1979.8, -5671.2], [-1949.1, -5735.9],
        [-1887.5, -5675.7], [-1817.7, -5758.3], [-1693.2, -5651.9],
        [-1256.9, -5516.1], [-1183.7, -5240.3], [-1319, -5193.8],
        [-1207.4, -4875.7], [-1171.4, -4641.8], [-989.4, -4492.8],
        [-746.3, -4501.9], [-559.4, -3659.9], [-1126.2, -3601.7],
        [-1701.4, -3402.8], [-1860.5, -3392.8], [-2189.7, -3446.9],
        [-2446.1, -4180.9], [-2705.1, -4736.8], [-3038, -5247.7],
        [-3698.3, -6023.5], [-4227.7, -6810.1], [-4155.7, -6909.3],
        [-4002.1, -7265.7], [-3982.4, -7475.3], [-3261.6, -7255.9],
        [-3254, -7654.9], [-2936.4, -7595.2],
      ]),
    },
    {
      id: "seskine",
      label: "Šeškinė",
      sourceNumber: 5,
      supportedFloorCounts: Object.freeze([5, 9, 12, 13, 16]),
      outer: points([
        [-2977, -3478], [-3151.5, -2869.3], [-2732.4, -2656.7],
        [-2866.5, -2297.5], [-2573, -2223.8], [-2636.8, -2056.7],
        [-2645.1, -1649.4], [-2836.7, -1485.5], [-2864.7, -1294.5],
        [-2682.8, -1286.7], [-2486.9, -1187.6], [-2395.5, -1183.7],
        [-2294.2, -1315.3], [-2321.6, -1376.3], [-2379, -1373.7],
        [-2388.6, -1435.5], [-2265.6, -1415.3], [-2113.2, -1498.2],
        [-2096.2, -1543.4], [-2020.9, -1588.6], [-2021.8, -1667.7],
        [-1915.2, -1676.4], [-1888.1, -1854.2], [-1811.5, -1857.7],
        [-1763.3, -1720.4], [-1615.9, -1549.6], [-1343.2, -1685.4],
        [-1267.4, -1593.4], [-1003.4, -1449.4], [-797.5, -1720.4],
        [-770.5, -1958.3], [-558.5, -2571.2], [-496.5, -3201.1],
        [-559.4, -3659.9], [-1126.2, -3601.7], [-1701.4, -3402.8],
        [-1981.5, -3409.4], [-2950.7, -3591.5], [-2977, -3478],
      ]),
    },
    {
      id: "pasilaiciai",
      label: "Pašilaičiai",
      sourceNumber: 7,
      supportedFloorCounts: Object.freeze([5, 9, 12, 13, 16]),
      outer: points([
        [-4545.3, -7244.5], [-3698.3, -6023.5], [-3131, -5362.6],
        [-2879, -5021.7], [-2548.1, -4427.8], [-2189.7, -3446.9],
        [-2960.9, -3594.2], [-3839, -3930.4], [-4689.2, -3970.8],
        [-4733.1, -3948.2], [-4932.3, -4057.9], [-4997.7, -4136.9],
        [-4959.3, -4256.1], [-4896.8, -4241.9], [-4739.7, -4346.5],
        [-4756.5, -5246], [-4818.2, -6249.3], [-5052.9, -6130.4],
        [-5190.8, -6182.4], [-5533.6, -5841.3], [-5554.4, -5790],
        [-5469.5, -5547], [-5495.4, -5453.6], [-5428.4, -5362.2],
        [-5439.8, -5192.2], [-5616.8, -5151.5], [-5770, -5202.3],
        [-6294, -5594.7], [-6437.7, -5846.4], [-6647.7, -6044.5],
        [-6547.4, -6076.2], [-6661.8, -6334.1], [-6835, -6470.2],
        [-6841.3, -6942.1], [-6766, -7023.7], [-6783.7, -7116.6],
        [-6392.1, -7008.4], [-6269.7, -6629], [-5870.2, -6860.7],
        [-6001.3, -7109.2], [-5706.6, -7280.6], [-5546.1, -7591.3],
        [-5318, -7222], [-5157.8, -7442.2], [-5156.3, -7499.7],
        [-4973.3, -7828.6], [-4545.3, -7244.5],
      ]),
    },
    {
      id: "justiniskes",
      label: "Justiniškės",
      sourceNumber: 8,
      supportedFloorCounts: STANDARD_PANEL_FLOOR_COUNTS,
      outer: points([
        [-4821.9, -3984.3], [-4733.1, -3948.2], [-4689.2, -3970.8],
        [-3839, -3930.4], [-2950.7, -3591.5], [-3101.2, -3032.2],
        [-3293, -3081.1], [-3708.5, -3109.9], [-4029.8, -2307.3],
        [-4064.2, -1900.8], [-4468.6, -1890.8], [-4459.4, -1566.9],
        [-4524.6, -1578.3], [-5016.6, -2367.3], [-5141, -2638.9],
        [-5104.8, -2749.6], [-5128.6, -2869.2], [-5071.8, -2972.5],
        [-5078.5, -3097.8], [-5019.8, -3061.6], [-4921.5, -3178.7],
        [-5014.3, -3402.9], [-4950.6, -3459.8], [-5017.9, -3631.7],
        [-4977.1, -3929.4], [-5036.3, -4017.1], [-5048.8, -4099.4],
        [-4997.7, -4136.9], [-4821.9, -3984.3],
      ]),
    },
    {
      id: "virsuliskes",
      label: "Viršuliškės",
      sourceNumber: 9,
      supportedFloorCounts: Object.freeze([5, 9, 12, 16]),
      outer: points([
        [-2732.4, -2656.7], [-2866.5, -2297.5], [-2573, -2223.8],
        [-2636.8, -2056.7], [-2645.1, -1649.4], [-2836.7, -1485.5],
        [-2864.7, -1294.5], [-3658.6, -1151.4], [-3886.1, -1145.3],
        [-4348.2, -1197.2], [-4512, -1256.8], [-4464.3, -1472.3],
        [-4468.6, -1890.8], [-4064.2, -1900.8], [-4029.8, -2307.3],
        [-3708.5, -3109.9], [-3293, -3081.1], [-3101.2, -3032.2],
        [-3151.5, -2869.3], [-2732.4, -2656.7],
      ]),
    },
    {
      id: "karoliniskes",
      label: "Karoliniškės",
      sourceNumber: 10,
      supportedFloorCounts: Object.freeze([5, 9, 12, 16]),
      outer: points([
        [-2411.2, -1174.9], [-2542.8, -1047], [-2713.3, -1042.4],
        [-2762.1, -884.6], [-2756, -673.6], [-2839.1, -664.6],
        [-2899, -522.6], [-2689.9, -327.6], [-2946.3, -68],
        [-3221.5, 62.1], [-3336.7, 154.8], [-3407, 273.2],
        [-3413.4, 353.2], [-3325.3, 522.3], [-3433.9, 592.1],
        [-3504.3, 708], [-3658, 592.6], [-3709.4, 632.2],
        [-3783.9, 540.7], [-3862.8, 505.6], [-3944.6, 373.7],
        [-4040.8, 374.2], [-4215.8, 426.2], [-4563.2, 595.7],
        [-4580.7, 717.1], [-4550.4, 817.8], [-4599.6, 945.5],
        [-4786.3, 982.8], [-5000.5, 1104.8], [-5104.5, 1010.8],
        [-5270.7, 965.4], [-5297.5, 883.8], [-5391.9, 853.5],
        [-5353.9, 642.5], [-4878.7, -358.6], [-4512, -1256.8],
        [-4348.2, -1197.2], [-3886.1, -1145.3], [-3658.6, -1151.4],
        [-2771.6, -1298.9], [-2411.2, -1174.9],
      ]),
    },
    {
      id: "lazdynai",
      label: "Lazdynai",
      sourceNumber: 11,
      supportedFloorCounts: Object.freeze([5, 9, 12, 16]),
      outer: points([
        [-3325.3, 522.3], [-3433.9, 592.1], [-3504.3, 708],
        [-3536.4, 707.1], [-3658, 592.6], [-3709.4, 632.2],
        [-3783.9, 540.7], [-3862.8, 505.6], [-3944.6, 373.7],
        [-4215.8, 426.2], [-4563.2, 595.7], [-4580.7, 717.1],
        [-4550.4, 817.8], [-4599.6, 945.5], [-4786.3, 982.8],
        [-5000.5, 1104.8], [-5104.5, 1010.8], [-5270.7, 965.4],
        [-5297.5, 883.8], [-5391.9, 853.5], [-5390.5, 776.8],
        [-5551.6, 685.1], [-5694, 930.7], [-6111.1, 1394.2],
        [-6195.3, 1588.6], [-6283.4, 1658], [-6342.1, 1787],
        [-6188.1, 1903.7], [-6114.5, 2046], [-6095, 2161.2],
        [-6110.5, 2234], [-6248.1, 2451.6], [-6564.8, 2778.9],
        [-6596.8, 2970.9], [-6500.8, 3370.8], [-6443.2, 3479.6],
        [-6315.3, 3555.5], [-5934.5, 3552.3], [-5729.8, 3589.6],
        [-5611.4, 3660], [-5441.8, 3845.6], [-4968.3, 4111.1],
        [-4827.6, 4337.2], [-4661.2, 4474.8], [-4546, 4519.5],
        [-4456.4, 4525.9], [-4382.9, 4493.9], [-4155.7, 4183.6],
        [-3912.5, 3981.1], [-3688.6, 3926.7], [-3586.2, 3869.1],
        [-3503, 3789.1], [-3435.8, 3475.6], [-3282.3, 3271.9],
        [-3291.9, 3105.5], [-3365.5, 2878.4], [-3368.7, 2636.3],
        [-3060.5, 2055.2], [-2952.7, 1601.9], [-2959.1, 963.2],
        [-3163.9, 649.6], [-3325.3, 522.3],
      ]),
    },
    {
      id: "antakalnis",
      label: "Antakalnis",
      sourceNumber: 13,
      supportedFloorCounts: STANDARD_PANEL_FLOOR_COUNTS,
      outer: points([
        [8676.6, -2819.6], [8347.4, -2670.4], [8069.1, -2365.1],
        [7642.4, -2197], [6760, -2278.2], [6288.1, -2067.3],
        [5844.2, -2043.3], [5393.3, -1762.4], [5209.3, -1837.3],
        [4989.4, -1770.4], [4533.5, -1349.4], [4069.1, -633.8],
        [3679.1, -727.7], [3457.5, -702], [3088.6, -875.4],
        [2599.1, -352], [2497.8, -118.2], [1781.3, 62.4],
        [1707, -74.7], [1736, -484.6], [1519, -715.2],
        [1777.4, -1269.8], [1777.4, -1752.3], [2030.1, -2471.8],
        [2150.1, -3094], [2065.4, -3592.5], [2194.9, -4104.2],
        [1849.8, -4531.1], [1427.5, -4707.1], [1008.2, -4651.8],
        [846, -4805.3], [778.8, -4984.5], [755.6, -5577.6],
        [829.2, -5938], [755.6, -6318.7], [922, -6456.3],
        [1229.1, -6421.1], [1680.2, -6540.5], [1987.4, -6489.3],
        [2348.9, -6601.3], [2707.2, -6841.2], [3417.5, -6799.6],
        [3609.5, -6889.2], [4006.2, -7576], [4115, -7585.6],
        [4655.6, -7274.1], [4719.6, -7386.1], [4646, -7658.1],
        [4796.4, -8064.4], [4764.4, -8415.2], [4860.4, -8642.4],
        [5049.2, -8744.7], [5673, -8723.3], [5961, -8902.5],
        [6239.3, -9225.6], [6332.1, -9920.9], [5946.2, -10435.6],
        [5654.3, -11904.3], [5408.3, -12420.2], [4934.4, -12982.1],
        [5060.6, -13528.1], [5023.1, -14027.5], [4780.2, -14616.8],
        [4800.7, -14806], [5389, -15417.7], [5496.4, -15353.4],
        [5013.2, -14906.6], [5046.9, -14370.7], [5211, -14297.5],
        [6129.6, -14302.9], [6134.7, -13183.9], [8218, -13182.3],
        [8125.3, -13016.2], [8639, -13003.3], [8627, -12505],
        [11149.8, -12833.8], [11213.5, -12399.8], [11060.4, -6057.1],
        [10835.9, -6046.2], [10573.4, -6322.7], [10349, -6400.2],
        [10178, -6161.9], [10212.8, -5971.2], [9493.3, -5811.8],
        [9548.1, -5493.5], [9365.4, -5412.8], [9423, -4798.4],
        [8955.4, -4664.8], [8955.5, -4590.9], [8551.4, -4596],
        [8565.3, -4180], [8469.6, -4068.6], [8309, -4061.6],
        [8335.7, -3448.3], [8571.9, -3501.5], [8602.1, -2935.1],
        [8676.6, -2819.6],
      ]),
    },
  ]);

export const SOVIET_PANEL_STYLE_ZONE_SOURCE = Object.freeze({
  id: "vilnius-plan-eldership-boundaries-2026-08-03",
  title: "Vilniaus miesto seniūnijų ribos",
  url:
    "https://zemelapiai.vplanas.lt/arcgis/rest/services/Open_Data/Vilniaus_miesto_ribos/MapServer/1",
  crs: "EPSG:3346",
  simplificationToleranceM: 20,
  antakalnisSimplificationToleranceM: 50,
  planningContextUrl:
    "https://vilnius.lt/rest/assets/7fa642ee-5af8-4ddb-90cd-317c00d83933",
  localOriginLks94: Object.freeze({ easting: 582400, northing: 6062080 }),
});

/**
 * Original Skansenas / Medinės Šnipiškės core in world-local metres.
 *
 * The reviewed envelope follows the checked-in GRPK alignments for Krokuvos,
 * Giedraičių, Saracėnų and Daugėliškio streets. It covers the roughly
 * eight-hectare historic core described by Vilnius heritage sources. Membership
 * alone is never enough to change a building: the candidate check below also
 * requires the compact, low, rectangular morphology of the documented
 * 1880–1930 East-Lithuanian suburban timber house.
 */
export const SNIPISKES_WOODEN_CORE = Object.freeze({
  id: "medines-snipiskes-original-core",
  label: "Medinės Šnipiškės",
  heritageRegisterCode: 12599,
  outer: points([
    [-25, -1_005],
    [225, -1_005],
    [225, -1_420],
    [-25, -1_430],
    [-25, -1_005],
  ]),
});

export const SNIPISKES_WOODEN_CORE_SOURCE = Object.freeze({
  cityReferenceUrl:
    "https://vilnius.lt/naujienos/vilniaus-skansenas-tampa-medinemis-snipiskemis-bus-kuriama-leto-gyvenimo-ir-medines-architekturos-oaze",
  visitorReferenceUrl:
    "https://www.govilnius.lt/aplankykite/marsrutai/pasivaiksiojimas-po-snipiskes/medines-snipiskes",
  heritageRegisterCode: 12599,
  sourceStreetNames: Object.freeze([
    "Krokuvos g.",
    "Giedraičių g.",
    "Saracėnų g.",
    "Daugėliškio g.",
  ]),
  localOriginLks94: Object.freeze({ easting: 582400, northing: 6062080 }),
});

/**
 * Reviewed southern Naujamiestis factory-belt envelope in world-local metres.
 *
 * The city identifies a roughly 42 ha industrial and railway-support area
 * between Panerių, Švitrigailos, Prūsų and Iešmininkų streets. These vertices
 * are taken from the checked-in GRPK street centrelines at their junctions and
 * bends; the polygon is a style guardrail, not a cadastral boundary. A second
 * morphology check prevents ordinary apartment buildings inside the broad
 * street envelope from being relabelled as factories.
 */
export const NAUJAMIESTIS_INDUSTRIAL_STYLE_ENVELOPE = Object.freeze({
  id: "naujamiestis-panerių-factory-belt",
  label: "Naujamiesčio pramoninis grįžratis",
  outer: points([
    [-1_467.791, 2_487.753], // Panerių / Iešmininkų
    [-452.5, 2_122.625], // Panerių / Švitrigailos
    [-289.87, 2_501.689], // Švitrigailos / Prūsų
    [-877.747, 2_896.851], // Prūsų southern bend
    [-967.946, 2_922.434], // Prūsų / Iešmininkų approach
    [-1_280, 2_850.053],
    [-1_358.177, 2_816],
    [-1_467.791, 2_487.753],
  ]),
});

export const NAUJAMIESTIS_INDUSTRIAL_STYLE_SOURCE = Object.freeze({
  cityReferenceUrl:
    "https://vilnius.lt/naujienos/naujamiescio-transformacija-is-pramoninio-rajono-i-miesto-sirdi",
  factoryReferenceUrl:
    "https://vilnius.lt/naujienos/buve-vilniaus-fabrikai-virsta-biurais-ruosiamasi-lelijos-rekonstrukcijai",
  sourceStreetNames: Object.freeze([
    "Panerių g.",
    "Švitrigailos g.",
    "Prūsų g.",
    "Iešmininkų g.",
  ]),
  streetGeometrySourceId: "grpk-open-2026-07-03",
  localOriginLks94: Object.freeze({ easting: 582400, northing: 6062080 }),
});

/**
 * Reviewed Antakalnis suburban-villa pocket around Antakalnio g. 26B.
 *
 * The official conservation material describes this street fabric as one
 * storey plus attic, with timber and masonry houses, gabled or half-hipped
 * roofs, porches and verandas. The radius deliberately limits the treatment
 * to the documented pocket: Antakalnis as a whole also contains palaces,
 * monasteries, modern infill and post-war apartment estates.
 */
export const ANTAKALNIS_SUBURBAN_STYLE_POCKET = Object.freeze({
  id: "antakalnio-26b-suburban-villa-pocket",
  label: "Antakalnio istorinių vilų kišenė",
  anchor: Object.freeze({ x: 1_935.463, z: -1_225.883 }),
  radiusM: 275,
});

export const ANTAKALNIS_SUBURBAN_STYLE_SOURCE = Object.freeze({
  address: "Antakalnio g. 26B",
  addressPointSourceUrl:
    "https://zemelapiai.vplanas.lt/arcgis/rest/services/Open_Data/Adresiniai_taskai_OD/MapServer/0",
  conservationReferenceUrl:
    "https://vilnius.lt/rest/assets/9b054b90-e242-45e7-8aa0-9ef023a2e0b8",
  districtIdentityReferenceUrl:
    "https://vilnius.lt/naujienos/vilniaus-seniuniju-tapatybe-antakalnis-zalias-saugus-ir-charakteringas",
  localOriginLks94: Object.freeze({ easting: 582400, northing: 6062080 }),
});

export function sovietPanelResidentialStyleZoneAt(
  point: Readonly<LocalHorizontal>,
): SovietPanelResidentialStyleZone | undefined {
  return SOVIET_PANEL_RESIDENTIAL_STYLE_ZONES.find((zone) =>
    pointInRing(point, zone.outer)
  );
}

export function isSovietPanelBackgroundCandidate(
  building: Readonly<BuildingFootprintData>,
  tileOrigin: Readonly<LocalHorizontal>,
): boolean {
  const localCentroid = ringCentroid(building.footprint.outer);
  const zone = sovietPanelResidentialStyleZoneAt({
    x: tileOrigin.x + localCentroid.x,
    z: tileOrigin.z + localCentroid.z,
  });
  if (zone === undefined) {
    return false;
  }

  const metrics = footprintMetrics(building);
  if (
    metrics.areaM2 < 50 ||
    metrics.longestSpanM < 10 ||
    metrics.rectangularity < 0.55
  ) {
    return false;
  }

  if (building.heightM !== undefined) {
    const sourceFloorCount = Math.round(building.heightM / 3.15);
    return zone.supportedFloorCounts.includes(sourceFloorCount);
  }

  // Heightless source records need a much stronger slab signal. This avoids
  // assigning panel articulation to small houses, schools and service halls.
  return metrics.areaM2 >= 220 &&
    metrics.longestSpanM >= 35 &&
    metrics.aspectRatio >= 2.1 &&
    metrics.rectangularity >= 0.65;
}

export function isSnipiskesWoodenBackgroundCandidate(
  building: Readonly<BuildingFootprintData>,
  tileOrigin: Readonly<LocalHorizontal>,
): boolean {
  const localCentroid = ringCentroid(building.footprint.outer);
  const globalCentroid = {
    x: tileOrigin.x + localCentroid.x,
    z: tileOrigin.z + localCentroid.z,
  };
  if (!pointInRing(globalCentroid, SNIPISKES_WOODEN_CORE.outer)) {
    return false;
  }

  const metrics = footprintMetrics(building);
  if (
    metrics.areaM2 < 40 ||
    metrics.areaM2 > 320 ||
    metrics.longestSpanM < 8 ||
    metrics.longestSpanM > 30 ||
    metrics.aspectRatio > 4.5 ||
    metrics.rectangularity < 0.6
  ) {
    return false;
  }

  // Source heights are 3.15 m storey increments in these tiles. Preserve the
  // low-rise heritage character while leaving modern infill in its own family.
  return building.heightM === undefined || building.heightM <= 9.45;
}

export function isNaujamiestisIndustrialBackgroundCandidate(
  building: Readonly<BuildingFootprintData>,
  tileOrigin: Readonly<LocalHorizontal>,
): boolean {
  const localCentroid = ringCentroid(building.footprint.outer);
  const globalCentroid = {
    x: tileOrigin.x + localCentroid.x,
    z: tileOrigin.z + localCentroid.z,
  };
  if (
    !pointInRing(
      globalCentroid,
      NAUJAMIESTIS_INDUSTRIAL_STYLE_ENVELOPE.outer,
    )
  ) {
    return false;
  }

  const metrics = footprintMetrics(building);
  if (
    metrics.areaM2 < 220 ||
    metrics.areaM2 > 8_000 ||
    metrics.longestSpanM < 25 ||
    metrics.longestSpanM > 180 ||
    metrics.aspectRatio > 8 ||
    metrics.rectangularity < 0.55
  ) {
    return false;
  }

  // Source-backed low and mid-rise halls are preferred. Heightless records
  // need a larger and longer factory signal to avoid repainting residences.
  if (building.heightM !== undefined) {
    return building.heightM >= 6.3 && building.heightM <= 22.05;
  }
  return metrics.areaM2 >= 600 && metrics.longestSpanM >= 35;
}

export function isAntakalnisSuburbanBackgroundCandidate(
  building: Readonly<BuildingFootprintData>,
  tileOrigin: Readonly<LocalHorizontal>,
): boolean {
  const localCentroid = ringCentroid(building.footprint.outer);
  const globalCentroid = {
    x: tileOrigin.x + localCentroid.x,
    z: tileOrigin.z + localCentroid.z,
  };
  if (
    Math.hypot(
      globalCentroid.x - ANTAKALNIS_SUBURBAN_STYLE_POCKET.anchor.x,
      globalCentroid.z - ANTAKALNIS_SUBURBAN_STYLE_POCKET.anchor.z,
    ) > ANTAKALNIS_SUBURBAN_STYLE_POCKET.radiusM
  ) {
    return false;
  }

  const metrics = footprintMetrics(building);
  if (
    metrics.areaM2 < 75 ||
    metrics.areaM2 > 450 ||
    metrics.longestSpanM < 9 ||
    metrics.longestSpanM > 35 ||
    metrics.aspectRatio > 2.8 ||
    metrics.rectangularity < 0.55
  ) {
    return false;
  }

  if (building.heightM !== undefined) {
    return Math.abs(building.heightM - 3.15) < 0.02 ||
      Math.abs(building.heightM - 6.3) < 0.02;
  }
  return metrics.areaM2 >= 100;
}

function footprintMetrics(building: Readonly<BuildingFootprintData>): {
  readonly areaM2: number;
  readonly longestSpanM: number;
  readonly aspectRatio: number;
  readonly rectangularity: number;
} {
  let minX = Number.POSITIVE_INFINITY;
  let minZ = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxZ = Number.NEGATIVE_INFINITY;
  for (const point of building.footprint.outer) {
    minX = Math.min(minX, point.x);
    minZ = Math.min(minZ, point.z);
    maxX = Math.max(maxX, point.x);
    maxZ = Math.max(maxZ, point.z);
  }
  const widthM = maxX - minX;
  const depthM = maxZ - minZ;
  const longestSpanM = Math.max(widthM, depthM);
  const shortestSpanM = Math.min(widthM, depthM);
  const boundsAreaM2 = widthM * depthM;
  const areaM2 = polygonAreaM2(building.footprint);
  return {
    areaM2,
    longestSpanM,
    aspectRatio: shortestSpanM > 0
      ? longestSpanM / shortestSpanM
      : Number.POSITIVE_INFINITY,
    rectangularity: boundsAreaM2 > 0 ? areaM2 / boundsAreaM2 : 0,
  };
}

function points(values: readonly (readonly [number, number])[]): LinearRing {
  return Object.freeze(values.map(([x, z]) => Object.freeze({ x, z })));
}
